import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { paymentsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { CheckCircle, XCircle, Loader2, BookOpen, Video, Users, Crown, RefreshCw, Phone } from 'lucide-react';

export default function PaymentSuccess() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { refreshUser } = useAuth();
    const [status, setStatus] = useState('loading');
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [productType, setProductType] = useState('');
    const [attempts, setAttempts] = useState(0);

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        const product = searchParams.get('product') || 'course';
        setProductType(product);
        
        if (sessionId) {
            pollPaymentStatus(sessionId);
        } else {
            setStatus('failed');
        }
    }, [searchParams]);

    const pollPaymentStatus = async (sessionId, attempt = 0) => {
        const maxAttempts = 5;
        const pollInterval = 2000;

        if (attempt >= maxAttempts) {
            setStatus('failed');
            return;
        }

        try {
            const res = await paymentsAPI.getStatus(sessionId);
            setPaymentInfo(res.data);
            setProductType(res.data.product_type);

            if (res.data.payment_status === 'paid') {
                setStatus('success');
                await refreshUser();
                return;
            } else if (res.data.status === 'expired') {
                setStatus('failed');
                return;
            }

            setAttempts(attempt + 1);
            setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
        } catch (err) {
            console.error('Error checking payment status:', err);
            if (attempt >= maxAttempts - 1) {
                setStatus('failed');
            } else {
                setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
            }
        }
    };

    const getProductTitle = () => {
        switch (productType) {
            case 'book':
                return 'How To Become A Flight Attendant';
            case 'course':
                return 'Master Courses';
            case 'membership_basic':
                return 'Basic Membership';
            case 'membership_premium':
                return 'Premium Membership';
            case 'membership_vip':
                return 'VIP Membership';
            case 'coaching_phone':
                return 'Phone Coaching Session';
            case 'coaching_zoom':
                return 'Video Coaching Session';
            case 'reschedule_fee':
                return 'Session Rescheduled';
            default:
                return 'Your Purchase';
        }
    };

    const getIcon = () => {
        switch (productType) {
            case 'book':
                return <BookOpen className="h-4 w-4" />;
            case 'course':
                return <Video className="h-4 w-4" />;
            case 'membership_basic':
                return <Users className="h-4 w-4" />;
            case 'membership_premium':
            case 'membership_vip':
                return <Crown className="h-4 w-4" />;
            case 'coaching_phone':
                return <Phone className="h-4 w-4" />;
            case 'coaching_zoom':
                return <Video className="h-4 w-4" />;
            case 'reschedule_fee':
                return <RefreshCw className="h-4 w-4" />;
            default:
                return null;
        }
    };

    const getRedirectPath = () => {
        switch (productType) {
            case 'book':
                return '/book';
            case 'course':
                return '/live-classes';
            case 'membership_basic':
                return '/live-classes';
            case 'membership_premium':
            case 'membership_vip':
                return '/airlines';
            case 'coaching_phone':
            case 'coaching_zoom':
            case 'reschedule_fee':
                return '/coaching';
            default:
                return '/dashboard';
        }
    };

    const getRedirectLabel = () => {
        switch (productType) {
            case 'book':
                return 'Start Reading';
            case 'course':
                return 'View Live Classes';
            case 'membership_basic':
                return 'View Live Classes';
            case 'membership_premium':
            case 'membership_vip':
                return 'View Airline Links';
            case 'coaching_phone':
            case 'coaching_zoom':
            case 'reschedule_fee':
                return 'View My Bookings';
            default:
                return 'Go to Dashboard';
        }
    };

    const getSuccessMessage = () => {
        if (productType === 'reschedule_fee') {
            return 'Your session has been successfully rescheduled! Check your bookings for the updated date and time.';
        }
        return `Thank you for your purchase! You now have access to ${getProductTitle()}.`;
    };

    return (
        <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center py-12 px-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-md"
            >
                <Card className="p-8 text-center">
                    {status === 'loading' && (
                        <>
                            <Loader2 className="h-16 w-16 animate-spin text-primary mx-auto mb-6" />
                            <h1 className="font-heading text-2xl font-bold mb-2">
                                Processing Payment
                            </h1>
                            <p className="text-muted-foreground mb-4">
                                Please wait while we confirm your payment...
                            </p>
                            <p className="text-sm text-muted-foreground">
                                Attempt {attempts + 1} of 5
                            </p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                                <CheckCircle className="h-12 w-12 text-green-600" />
                            </div>
                            <h1 className="font-heading text-2xl font-bold mb-2">
                                Payment Successful!
                            </h1>
                            <p className="text-muted-foreground mb-6">
                                {getSuccessMessage()}
                            </p>
                            {paymentInfo && (
                                <div className="bg-muted rounded-lg p-4 mb-6 text-left">
                                    <p className="text-sm">
                                        <span className="text-muted-foreground">Product:</span>{' '}
                                        <span className="font-medium">{getProductTitle()}</span>
                                    </p>
                                    <p className="text-sm mt-1">
                                        <span className="text-muted-foreground">Amount:</span>{' '}
                                        <span className="font-medium">
                                            ${paymentInfo.amount_total} {paymentInfo.currency?.toUpperCase()}
                                        </span>
                                    </p>
                                    {(productType === 'membership_basic' || productType === 'membership_premium') && (
                                        <p className="text-sm mt-1">
                                            <span className="text-muted-foreground">Billing:</span>{' '}
                                            <span className="font-medium">Monthly</span>
                                        </p>
                                    )}
                                </div>
                            )}
                            <div className="space-y-3">
                                <Button 
                                    className="w-full rounded-full gap-2"
                                    onClick={() => navigate(getRedirectPath())}
                                    data-testid="go-to-content"
                                >
                                    {getIcon()}
                                    {getRedirectLabel()}
                                </Button>
                                <Button 
                                    variant="outline"
                                    className="w-full rounded-full"
                                    onClick={() => navigate('/dashboard')}
                                >
                                    Go to Dashboard
                                </Button>
                            </div>
                        </>
                    )}

                    {status === 'failed' && (
                        <>
                            <div className="h-20 w-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                                <XCircle className="h-12 w-12 text-red-600" />
                            </div>
                            <h1 className="font-heading text-2xl font-bold mb-2">
                                Payment Issue
                            </h1>
                            <p className="text-muted-foreground mb-6">
                                We couldn't confirm your payment. If you were charged, please contact support.
                            </p>
                            <div className="space-y-3">
                                <Button 
                                    className="w-full rounded-full"
                                    onClick={() => navigate(productType?.includes('membership') ? '/membership' : (productType === 'book' ? '/book' : '/course'))}
                                    data-testid="try-again"
                                >
                                    Try Again
                                </Button>
                                <Button 
                                    variant="outline"
                                    className="w-full rounded-full"
                                    onClick={() => navigate('/')}
                                >
                                    Go Home
                                </Button>
                            </div>
                        </>
                    )}
                </Card>
            </motion.div>
        </div>
    );
}
